import { useMutation } from 'react-query';
import axios from 'axios';

const url = process.env.REACT_APP_API_URL + '/api/v1';

const instance = axios.create({
  baseURL: url,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request throttling and deduplication
const pendingRequests = new Map();
const requestTimestamps = [];
const MAX_REQUESTS_PER_SECOND = 5;
const TIME_WINDOW = 1000; // 1 second

instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Rate limiting: prevent more than MAX_REQUESTS_PER_SECOND per second
    const now = Date.now();
    // Remove timestamps older than TIME_WINDOW
    while (requestTimestamps.length > 0 && requestTimestamps[0] < now - TIME_WINDOW) {
      requestTimestamps.shift();
    }

    if (requestTimestamps.length >= MAX_REQUESTS_PER_SECOND) {
      console.warn(`Rate limit exceeded: ${requestTimestamps.length} requests in the last second. Rejecting request.`);
      return Promise.reject(new Error('Too many requests. Please slow down.'));
    }

    requestTimestamps.push(now);

    // Request deduplication: prevent duplicate simultaneous requests
    const requestKey = `${config.method}:${config.url}:${JSON.stringify(config.params || {})}:${JSON.stringify(config.data || {})}`;

    if (pendingRequests.has(requestKey)) {
      console.warn(`Duplicate request detected: ${requestKey}. Using existing request.`);
      return pendingRequests.get(requestKey);
    }

    // Store the request promise
    const requestPromise = config;
    pendingRequests.set(requestKey, requestPromise);

    // Clean up after request completes
    const cleanup = () => pendingRequests.delete(requestKey);
    config.cleanup = cleanup;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to clean up pending requests
instance.interceptors.response.use(
  (response) => {
    if (response.config.cleanup) {
      response.config.cleanup();
    }
    return response;
  },
  (error) => {
    if (error.config?.cleanup) {
      error.config.cleanup();
    }
    return Promise.reject(error);
  },
);

export const useLogin = () =>
  useMutation({
    mutationFn: async (body) => {
      const form = new URLSearchParams();
      if (body?.username != null) form.append('username', body.username);
      if (body?.password != null) form.append('password', body.password);

      return axios.post(`${url}/auth/token`, form, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
    },
  });

export const useResetCredentialPassword = () =>
  useMutation({
    mutationFn: async (body) => {
      const { data } = await instance.post('/auth/reset-credential-password', body);
      return data;
    },
  });

export const useCreateApplication = () =>
  useMutation({
    mutationFn: async (body) => {
      const { data } = await instance.post('/application', body);
      return data;
    },
  });

export const useUpdateApplication = () =>
  useMutation({
    mutationFn: async ({ code, body }) => {
      const { data } = await instance.put(`/application/${code}`, body);
      return data;
    },
  });

export const useSetStatusApplication = () =>
  useMutation({
    mutationFn: async ({ code, is_active }) => {
      const { data } = await instance.patch(`/application/${code}/status`, {
        is_active,
      });
      return data;
    },
  });

export const useDeleteApplication = () =>
  useMutation({
    mutationFn: async (code) => {
      const { data } = await instance.delete(`/application/${code}`);
      return data;
    },
  });

export const useUploadApplicationImage = () =>
  useMutation({
    mutationFn: async ({ code, file }) => {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await instance.post(
        `/application/${code}/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data;
    },
  });

export const useCreateUser = () =>
  useMutation({
    mutationFn: async (body) => {
      const { data } = await instance.post('/user', body);
      return data;
    },
  });

export const useUpdateUser = () =>
  useMutation({
    mutationFn: async ({ username, body }) => {
      const { data } = await instance.put(`/user/${username}`, body);
      return data;
    },
  });

export const useDeleteUser = () =>
  useMutation({
    mutationFn: async (username) => {
      const { data } = await instance.delete(`/user/${username}`);
      return data;
    },
  });

export const useCreateRole = () =>
  useMutation({
    mutationFn: async ({ body }) => {
      const { data } = await instance.post('/role', body);
      return data;
    },
  });

export const useUpdateRole = () =>
  useMutation({
    mutationFn: async ({ code, body }) => {
      const { data } = await instance.put(`/role/${code}`, body);
      return data;
    },
  });

export const useSyncPermissions = () =>
  useMutation({
    mutationFn: async ({ code, permissions }) => {
      const { data } = await instance.post(`/role/${code}/permissions`, {
        permissions,
      });
      return data;
    },
  });

export const useDeleteRole = () =>
  useMutation({
    mutationFn: async (code) => {
      const { data } = await instance.delete(`/role/${code}`);
      return data;
    },
  });

export const useGetSecret = () =>
  useMutation({
    mutationFn: async (body) => {
      const { data } = await instance.post(`/auth/one-time-secret`, body);
      return data;
    },
  });

export const useLoginApps = () =>
  useMutation({
    mutationFn: async ({ originUsername, body }) => {
      const { data } = await instance.post(
        `/user/${originUsername}/credential`,
        body,
      );
      return data;
    },
  });

export const useFetchActivityLogs = () =>
  useMutation({
    mutationFn: async ({ limit, page, method, url, resp_status, user_id }) => {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('page', page);

      if (method) params.append('method', method);
      if (url) params.append('url', url);
      if (resp_status) params.append('resp_status', resp_status);
      if (user_id) params.append('user_id', user_id);

      const { data } = await instance.post(`/logs/?${params.toString()}`);
      return data;
    },
  });
